import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconBell, IconSpeakerphone } from "@tabler/icons-react";
import { Button, Text } from "@fragments";
import { ReactComponent as FullLogo } from "@assets/logo/M_WHITE_LOGO_FULL.svg";
import getDefaultNotifications from "./_defaultNotifications";
import AICircle from "@components/AICircle";
import "./styles.scss";


// Labels are resolved at render (`t(cat.labelKey)`) so they follow the language.
const NOTIFICATION_CATEGORIES = [
    { key: "all", labelKey: "notifications.categories.all" },
    { key: "posts", labelKey: "notifications.categories.posts" },
    { key: "messages", labelKey: "notifications.categories.messages" },
    { key: "updates", labelKey: "notifications.categories.updates" },
];

export default function Notifications() {
    const { t } = useTranslation("shell");
    const [open, setOpen] = useState(false);
    const [category, setCategory] = useState("all");
    // `t` changes identity on language change, so the defaults re-localise.
    const notifications = useMemo(() => getDefaultNotifications(t), [t]);

    const popupRef = useRef(null);
    const btnRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (
                popupRef.current &&
                !popupRef.current.contains(e.target) &&
                btnRef.current &&
                !btnRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        }
        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    const filteredNotifications = useMemo(() => {
        if (category === "all") return notifications;
        return notifications.filter(n => n.category === category);
    }, [notifications, category]);

    const counts = useMemo(() => {
        const c = { all: notifications.length };
        NOTIFICATION_CATEGORIES.forEach(cat => {
            if (cat.key !== "all") {
                c[cat.key] = notifications.filter(n => n.category === cat.key).length;
            }
        });
        return c;
    }, [notifications]);

    return (
        <div className="notifications-wrapper">
            <div ref={btnRef}>
                <Button
                    color="overlay"
                    variant="rounded"
                    icon={<IconBell />}
                    onClick={() => setOpen(prev => !prev)}
                    badge={notifications.length > 0 ? notifications.length : undefined}
                    tooltip={t("notifications.tooltip")}
                />
            </div>

            {open && (
                <div className="notifications-popup" ref={popupRef}>
                    <div className="notifications-popup-header">
                        <Text.h4
                            left={<IconBell />}
                            value={t("notifications.title")}
                        />
                    </div>

                    <div className="notifications-categories">
                        {NOTIFICATION_CATEGORIES.map(cat => (
                            <Button
                                key={cat.key}
                                variant="default"
                                size="small"
                                color={category === cat.key ? "inverse" : "overlay"}
                                onClick={() => setCategory(cat.key)}
                            >
                                {t(cat.labelKey)} {counts[cat.key] > 0 && `(${counts[cat.key]})`}
                            </Button>
                        ))}
                    </div>

                    <div className="notifications-list">
                        {filteredNotifications.length > 0 ? (
                            filteredNotifications.map(notif => (
                                <div key={notif.id} className="notification-item" onClick={() => notif.handler?.type === 'link' && window.open(notif.handler.value)}>
                                    {notif.avatar === 'platform' ? (
                                        <div className="notification-platform-icon">
                                            <FullLogo />
                                        </div>
                                    ) : notif.avatar === 'ai' ? (
                                        <div className="notification-ai-icon">
                                            <AICircle size={28} />
                                        </div>
                                    ) : (
                                        <img src={notif.avatar} className="notification-user-avatar" />
                                    )}
                                    <div className="notification-content">
                                        <Text.span weight={600} value={notif.title} />
                                        <Text.span maxLines={2} contrast={5} value={notif.description} />
                                    </div>
                                    <div className="notification-icon">
                                        {notif.icon === "campaign" && <IconSpeakerphone />}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="px-3 py-4 text-center">
                                <Text.span contrast={5} value={t("notifications.empty")} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
